import React, { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { createApplication } from "../lib/api";

const ROLES = [
  { v: "videographer", label: "Videographer" },
  { v: "photographer", label: "Photographer" },
  { v: "editor", label: "Editor" },
  { v: "creator", label: "Content Creator" },
];

const AVAIL = [
  { v: "full-time", label: "Full-time" },
  { v: "part-time", label: "Part-time" },
  { v: "freelance", label: "Freelance" },
  { v: "weekends", label: "Weekends" },
];

const initial = {
  full_name: "",
  email: "",
  phone: "",
  city: "",
  portfolio_link: "",
  linkedin_url: "",
  role: "videographer",
  availability: "freelance",
  message: "",
};

export const JoinCompany = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const rotateX = useTransform(scrollYProgress, [0, 0.5], [10, 0]);

  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.city) {
      toast.error("Please fill name, email, phone and city.");
      return;
    }
    try {
      setLoading(true);
      await createApplication(form);
      toast.success("Successfully sent. The BlinkShots team will contact you shortly.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="join"
      ref={ref}
      data-testid="join-section"
      className="relative py-28 md:py-40 perspective-scene"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,#100202,transparent_60%)]" />
      <div
        aria-hidden
        className="absolute top-10 -left-40 h-[420px] w-[420px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #4ea8ff, transparent 70%)" }}
      />
      <div
        aria-hidden
        className="absolute bottom-10 -right-40 h-[360px] w-[360px] rounded-full blur-[120px] opacity-25 pointer-events-none"
        style={{ background: "radial-gradient(closest-side, #ff3d8e, transparent 70%)" }}
      />
      <div className="neon-streak top-[6%] left-[-10%] right-[-10%]" />

      <div className="relative mx-auto max-w-7xl px-5 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <motion.div
          style={{ rotateX }}
          className="lg:col-span-5 flex flex-col justify-center"
        >
          <p className="mono-eyebrow mb-4" data-testid="join-eyebrow">
            Join the Company
          </p>
          <h2 className="font-display text-4xl md:text-6xl text-white leading-[1.02] tracking-tight">
            Join <span className="text-blink-gradient">BlinkShots</span>.
          </h2>
          <p className="mt-5 text-neutral-400 font-body text-base md:text-lg max-w-md">
            Are you a creator ready to work on real projects and build with a fast-growing content
            brand? Apply below — we read every application.
          </p>

          <div className="mt-10 space-y-5">
            {[
              { k: "For", v: "Videographers · Photographers · Editors · Creators" },
              { k: "Where", v: "Remote + On-site shoots worldwide" },
              { k: "Pay", v: "Per-project · Flexible rates" },
            ].map((i) => (
              <div key={i.k} className="flex items-start gap-4 border-l border-white/10 pl-4">
                <span className="mono-eyebrow w-16 flex-shrink-0">{i.k}</span>
                <span className="text-neutral-200">{i.v}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 40, rotateX: 10 }}
          whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          className="lg:col-span-7 relative rounded-3xl glass p-6 md:p-10"
          data-testid="join-form"
        >
          <div className="absolute -top-20 -right-20 h-44 w-44 rounded-full bg-[#E10600]/15 blur-3xl" />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 relative">
            <Field
              label="Full name"
              value={form.full_name}
              onChange={(v) => setField("full_name", v)}
              name="full_name"
              required
            />
            <Field
              label="Email"
              type="email"
              value={form.email}
              onChange={(v) => setField("email", v)}
              name="email"
              required
            />
            <Field
              label="Phone"
              value={form.phone}
              onChange={(v) => setField("phone", v)}
              name="phone"
              required
            />
            <Field
              label="City / location"
              value={form.city}
              onChange={(v) => setField("city", v)}
              name="city"
              required
            />
            <Field
              label="Portfolio link"
              value={form.portfolio_link}
              onChange={(v) => setField("portfolio_link", v)}
              name="portfolio_link"
              placeholder="https://"
            />
            <Field
              label="LinkedIn"
              value={form.linkedin_url}
              onChange={(v) => setField("linkedin_url", v)}
              name="linkedin_url"
              placeholder="https://linkedin.com/in/..."
            />

            <SelectField
              label="Interested role"
              options={ROLES}
              value={form.role}
              onChange={(v) => setField("role", v)}
              name="role"
            />
            <SelectField
              label="Availability"
              options={AVAIL}
              value={form.availability}
              onChange={(v) => setField("availability", v)}
              name="availability"
            />

            <div className="md:col-span-2">
              <TextArea
                label="Short message / experience"
                value={form.message}
                onChange={(v) => setField("message", v)}
                name="message"
                rows={4}
              />
            </div>
          </div>

          <div className="relative mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-xs text-neutral-500 font-mono max-w-md">
              By applying you agree to be contacted about future BlinkShots projects.
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="join-submit-button"
              className="btn-3d group inline-flex items-center gap-2 rounded-full bg-[#E10600] px-7 py-4 text-sm font-medium text-white disabled:opacity-70"
            >
              {loading ? (
                <Loader2 className="relative z-10 h-4 w-4 animate-spin" />
              ) : (
                <ArrowUpRight className="relative z-10 h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              )}
              <span className="relative z-10">{loading ? "Sending" : "Become a Creator"}</span>
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ label, value, onChange, name, type = "text", required, placeholder }) => (
  <label className="flex flex-col gap-1.5">
    <span className="mono-eyebrow text-neutral-500">
      {label} {required && <span className="text-[#E10600]">*</span>}
    </span>
    <input
      type={type}
      value={value}
      required={required}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`join-input-${name}`}
      className="bg-transparent border-b border-white/10 py-2 text-white placeholder:text-neutral-600 focus:border-[#E10600] focus:outline-none transition-colors duration-300 font-body"
    />
  </label>
);

const SelectField = ({ label, options, value, onChange, name }) => (
  <label className="flex flex-col gap-1.5">
    <span className="mono-eyebrow text-neutral-500">{label}</span>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`join-select-${name}`}
        className="w-full appearance-none bg-transparent border-b border-white/10 py-2 pr-8 text-white focus:border-[#E10600] focus:outline-none font-body cursor-pointer"
      >
        {options.map((o) => (
          <option key={o.v} value={o.v} className="bg-[#0a0a0a] text-white">
            {o.label}
          </option>
        ))}
      </select>
      <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-neutral-500">
        ▾
      </span>
    </div>
  </label>
);

const TextArea = ({ label, value, onChange, name, rows = 3 }) => (
  <label className="flex flex-col gap-1.5">
    <span className="mono-eyebrow text-neutral-500">{label}</span>
    <textarea
      rows={rows}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={`join-textarea-${name}`}
      className="bg-transparent border-b border-white/10 py-2 text-white placeholder:text-neutral-600 focus:border-[#E10600] focus:outline-none transition-colors duration-300 font-body resize-none"
      placeholder="Tell us what you love to shoot / edit..."
    />
  </label>
);

export default JoinCompany;
